<script>
  import { onMount } from "svelte";
  import { ComboBox, DataTable } from "carbon-components-svelte";
  import {
    Pagination,
    Select,
    SelectItem,
    Tag,
    TextInput,
  } from "paxray-components-svelte";
  import { PLUS_ICON } from "@utils/icons.js";
  import { Button } from "paxray-components-svelte";
  import Modal from "lib/components/Modal.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import TrashIcon from "lib/components/common/icons/app/TrashIcon.svelte";

  // Loaded entries, bound by the parent for the CSV export.
  export let entries = [];
  // "titles" or "elements".
  export let kind = "titles";

  const statusLabels = { pending: "Pending", approved: "Approved" };
  const sourceLabels = { check: "Check", admin: "Admin", import: "Import" };
  $: connected = true;
  $: endpoint =
    kind === "elements"
      ? "/api/damprojects/admin/safe-ui-elements"
      : "/api/damprojects/admin/safe-windowtitles";
  $: headers = [
    ...(kind === "elements"
      ? [
          { key: "text", value: "Text" },
          { key: "applicationname", value: "Application", sort: compareApps },
          { key: "type", value: "Element type" },
          { key: "elementclass", value: "Element class" },
          { key: "input_source", value: "Field" },
        ]
      : [
          { key: "windowtitle", value: "Title" },
          { key: "applicationname", value: "Application", sort: compareApps },
        ]),
    { key: "status", value: "Status", sort: false },
    { key: "source", value: "Added in" },
    { key: "created_by", value: "Added by" },
    { key: "created_when", value: "Added on" },
    { key: "actions", value: "Actions", sort: false },
  ];
  // Sort applications by their readable name, as shown in the table.
  $: compareApps = (a, b) =>
    appLabel(a, clearnames).localeCompare(appLabel(b, clearnames));
  const statusOptions = [
    { id: "pending", text: "Pending" },
    { id: "approved", text: "Approved" },
  ];

  let loading = true;
  let errorOpen = false;
  let errorMessage = "";
  let saving = false;
  let search = "";
  let statusFilter = null;
  let applicationFilter = null;
  let page = 1;
  let pageSize = 10;
  let addOpen = false;
  let deleteOpen = false;
  let deleteEntry = null;
  let form = {
    applicationname: "",
    windowtitle: "",
    type: "",
    elementclass: "",
    input_source: "name",
    text: "",
  };
  // Technical application name to readable name, as in the anonymization check.
  let clearnames = new Map();

  $: applicationOptions = [
    ...new Set(entries.map((entry) => entry.applicationname)),
  ]
    .sort()
    .map((value) => ({ id: value, text: appLabel(value, clearnames) }))
    .sort((a, b) => a.text.localeCompare(b.text));
  $: rows = entries
    .filter(
      (entry) =>
        (!statusFilter || entry.status === statusFilter) &&
        (!applicationFilter || entry.applicationname === applicationFilter) &&
        `${textOf(entry)} ${entry.applicationname} ${appLabel(entry.applicationname, clearnames)}`
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
    )
    // Pending entries first, they are the ones waiting for a review.
    .sort(
      (a, b) =>
        (a.status === "pending" ? 0 : 1) - (b.status === "pending" ? 0 : 1) ||
        textOf(a).localeCompare(textOf(b), "en", { numeric: true }),
    );
  $: if (page > Math.max(1, Math.ceil(rows.length / pageSize))) page = 1;
  $: search, statusFilter, applicationFilter, (page = 1);
  $: addDisabled =
    saving ||
    !form.applicationname.trim() ||
    (kind === "elements" ? !form.text.trim() : !form.windowtitle.trim());

  onMount(() => {
    if (connected) load();
    else loading = false;
    loadClearnames();
  });

  function textOf(entry) {
    return String(entry.windowtitle ?? entry.text ?? "");
  }

  async function loadClearnames() {
    const response = await fetchWithSession(
      "/api/damprojects/getAppnameMapping",
    );
    if (!response.ok) return;
    const mappings = await response.json();
    clearnames = new Map(
      mappings
        .filter((mapping) => mapping?.applicationname && mapping?.clearname)
        .map((mapping) => [String(mapping.applicationname), mapping.clearname]),
    );
  }

  function appLabel(applicationname, names) {
    return names.get(String(applicationname)) || applicationname;
  }

  async function load() {
    loading = true;
    try {
      const response = await fetchWithSession(endpoint);
      if (response.ok) entries = await response.json();
    } finally {
      loading = false;
    }
  }

  function replaceEntry(updated) {
    entries = entries.some((entry) => entry.id === updated.id)
      ? entries.map((entry) => (entry.id === updated.id ? updated : entry))
      : [...entries, updated];
  }

  async function mutate(suffix, options) {
    if (saving) return null;
    saving = true;
    try {
      const response = await fetchWithSession(`${endpoint}${suffix}`, {
        ...options,
        suppressErrorToast: true,
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Unable to update allowlist");
      return data;
    } catch (error) {
      errorMessage = error.message;
      errorOpen = true;
      return null;
    } finally {
      saving = false;
    }
  }

  async function approve(entry) {
    const data = await mutate(`/${entry.id}`, {
      method: "PATCH",
      body: JSON.stringify({}),
    });
    if (data) replaceEntry(data);
  }

  async function remove() {
    if (!deleteEntry) return;
    const data = await mutate(`/${deleteEntry.id}`, { method: "DELETE" });
    if (!data) return;
    entries = entries.filter((entry) => entry.id !== deleteEntry.id);
    deleteOpen = false;
    deleteEntry = null;
  }

  async function add() {
    if (addDisabled) return;
    const data = await mutate("", {
      method: "POST",
      body: JSON.stringify(form),
    });
    if (!data) return;
    replaceEntry(data);
    addOpen = false;
  }

  function openAdd() {
    form = {
      applicationname: "",
      windowtitle: "",
      type: "",
      elementclass: "",
      input_source: "name",
      text: "",
    };
    addOpen = true;
  }

  function resetFilters() {
    search = "";
    statusFilter = null;
    applicationFilter = null;
  }

  function shouldFilterItem(item, value) {
    return item.text.toLowerCase().includes(value.toLowerCase());
  }

  function formatDate(value) {
    return value ? new Date(value).toLocaleDateString() : "";
  }
</script>

<section class="safe-titles">
  <div class="toolbar">
    <div class="scope-filter">
      <ComboBox
        id="safe-titles-status-filter"
        titleText="Status"
        placeholder="All statuses"
        items={statusOptions}
        bind:selectedId={statusFilter}
        {shouldFilterItem}
      />
    </div>
    <div class="scope-filter">
      <ComboBox
        id="safe-titles-application-filter"
        titleText="Application"
        placeholder="All applications"
        items={applicationOptions}
        bind:selectedId={applicationFilter}
        {shouldFilterItem}
      />
    </div>
    <div class="search">
      <TextInput
        id="safe-titles-search"
        type="search"
        size="medium"
        labelText={kind === "elements" ? "Search UI elements" : "Search titles"}
        bind:value={search}
      />
    </div>
    <button
      type="button"
      class="reset-filter-button"
      title="Reset filters"
      aria-label="Reset filters"
      on:click={resetFilters}
      disabled={!search && !statusFilter && !applicationFilter}
    >
      <img src="/img/assistant/icons/reset.svg" alt="" />
    </button>
    <Button
      text={kind === "elements" ? "Add UI element" : "Add title"}
      kind="secondary"
      size="field"
      iconLeft
      icon={PLUS_ICON}
      disabled={!connected}
      on:click={openAdd}
    />
  </div>
  <div class="table-scroll">
    <DataTable
      zebra
      sortable
      class="custom-table zebra-rows"
      {headers}
      {rows}
      {page}
      {pageSize}
      size="short"
    >
      <svelte:fragment slot="cell" let:cell let:row>
        {#if cell.key === "actions"}
          <div class="row-actions">
            {#if row.status === "pending"}
              <button
                type="button"
                class="table-action-button"
                title="Approve for all projects"
                aria-label="Approve for all projects"
                on:click={() => approve(row)}
              >
                <img src="/img/assistant/icons/check.svg" alt="" />
              </button>
            {:else}
              <span></span>
            {/if}
            <button
              type="button"
              class="table-action-button danger"
              title="Remove from allowlist"
              aria-label="Remove from allowlist"
              on:click={() => {
                deleteEntry = row;
                deleteOpen = true;
              }}><TrashIcon /></button
            >
          </div>
        {:else if cell.key === "applicationname"}
          <span class="cell-value" title={cell.value}
            >{appLabel(cell.value, clearnames)}</span
          >
        {:else if cell.key === "status"}
          <span class="status {cell.value}">
            <Tag size="sm">{statusLabels[cell.value] || cell.value}</Tag>
          </span>
        {:else if cell.key === "source"}
          {sourceLabels[cell.value] || cell.value}
        {:else if kind === "elements" && ["type", "elementclass"].includes(cell.key) && !cell.value}
          <span class="cell-value">None</span>
        {:else if cell.key === "created_when"}
          {formatDate(cell.value)}
        {:else}
          <span class="cell-value">{cell.value || ""}</span>
        {/if}
      </svelte:fragment>
    </DataTable>
    {#if rows.length === 0}
      <p class="empty-state">
        {!connected
          ? "Not connected yet."
          : loading
            ? kind === "elements"
              ? "Loading UI allowlist..."
              : "Loading title allowlist..."
            : entries.length
              ? "No entries match your search."
              : kind === "elements"
                ? "No UI elements in the allowlist yet."
                : "No titles in the allowlist yet."}
      </p>
    {/if}
    <div class="table-pagination">
      <Pagination
        dark
        bind:page
        bind:pageSize
        totalItems={rows.length}
        pageSizes={[10, 25, 50]}
      />
    </div>
  </div>
</section>

<Modal
  bind:open={addOpen}
  modalHeading={kind === "elements"
    ? "Add UI element to allowlist"
    : "Add title to allowlist"}
  actions
  autowidth
  primaryButtonText="Add"
  primaryButtonVariant="secondary"
  primaryButtonValidations={addDisabled}
  primaryButtonAction={add}
>
  <div class="editor">
    <TextInput
      id="safe-titles-application"
      labelText="Application"
      bind:value={form.applicationname}
      description="The program name as recorded, for example excel or explorer."
    />
    {#if kind === "elements"}
      <TextInput
        id="safe-ui-type"
        labelText="Element type"
        bind:value={form.type}
        description="Leave empty if the element has no type."
      />
      <TextInput
        id="safe-ui-class"
        labelText="Element class"
        bind:value={form.elementclass}
        description="Leave empty if the element has no class."
      />
      <Select
        id="safe-ui-source"
        labelText="Checked field"
        bind:selected={form.input_source}
      >
        <SelectItem value="name" text="Name" />
        <SelectItem value="automationid" text="Automation ID" />
      </Select>
      <TextInput id="safe-ui-text" labelText="Text" bind:value={form.text} />
      <p>
        Only add generic text that is safe in every project. Elements with
        exactly this application, type, class, field and text are not
        anonymized.
      </p>
    {:else}
      <TextInput
        id="safe-titles-title"
        labelText="Title"
        bind:value={form.windowtitle}
        description="The exact title. It is approved right away and checked in every new project."
      />
    {/if}
  </div>
</Modal>

<Modal
  bind:open={deleteOpen}
  modalHeading="Remove from allowlist?"
  actions
  autowidth
  primaryButtonValidations={saving}
  primaryButtonText="Remove"
  primaryButtonVariant="secondary"
  primaryButtonAction={remove}
>
  <p>
    Remove “{deleteEntry ? textOf(deleteEntry) : ""}” from the allowlist? It
    will be reviewed again in new projects.
  </p>
</Modal>

<Modal
  bind:open={errorOpen}
  modalHeading="Unable to update allowlist"
  actions
  autowidth
  noSecondaryButton
  primaryButtonText="Close"
  primaryButtonVariant="secondary"
  primaryButtonAction={() => (errorOpen = false)}
>
  <p>{errorMessage}</p>
</Modal>

<style>
  .safe-titles,
  .editor {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    min-width: 0;
  }
  p {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
  }
  .toolbar {
    display: flex;
    align-items: flex-end;
    gap: var(--control-gap);
    flex-wrap: wrap;
  }
  .search {
    flex: 1;
    min-width: 12rem;
  }
  .scope-filter {
    flex: 1 1 12rem;
    min-width: 12rem;
  }
  /* The input already draws the border, so the list box around it needs none. */
  .scope-filter :global(.bx--combo-box.bx--list-box) {
    border: 0 !important;
    background: transparent;
  }
  .reset-filter-button {
    display: flex;
    flex: 0 0 2.5rem;
    width: 2.5rem;
    height: 2.5rem;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.5rem;
    background: var(--pax-black);
    cursor: pointer;
  }
  .reset-filter-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .table-scroll {
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid var(--pax-smoke);
    border-radius: 0.5rem;
    background: var(--pax-white);
  }
  .row-actions {
    display: grid;
    grid-template-columns: repeat(2, 2rem);
    justify-content: center;
    gap: var(--control-gap);
  }
  .table-action-button {
    box-sizing: border-box;
    display: grid;
    width: 2rem;
    height: 2rem;
    place-items: center;
    padding: 0;
    border: 0;
    border-radius: 0.35rem;
    background: var(--pax-black);
    cursor: pointer;
  }
  .table-action-button img {
    width: 0.875rem;
  }
  .table-action-button :global(svg) {
    width: 1.125rem;
    height: 1.125rem;
    fill: var(--pax-white);
  }
  .table-action-button.danger:hover {
    background: var(--pax-danger);
  }
  .table-action-button:focus-visible {
    outline: 2px solid var(--pax-black);
    outline-offset: 0.125rem;
  }
  .cell-value {
    display: block;
    max-width: 28rem;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
  .status :global(.bx--tag) {
    margin: 0;
    background-color: var(--pax-blue-new);
    color: var(--pax-black);
  }
  .status.approved :global(.bx--tag) {
    background-color: var(--pax-green-new);
  }
  .status.pending :global(.bx--tag) {
    background-color: var(--pax-yellow-new);
  }
  .table-pagination {
    overflow: hidden;
  }
  .table-pagination :global(.bx--pagination) {
    box-sizing: border-box;
    width: 100%;
    min-height: 2.5rem;
    border: 0;
    border-top: 1px solid var(--pax-sub-new);
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--pagination__text) {
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--select-input) {
    border-color: var(--pax-sub-new) !important;
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--select__arrow) {
    fill: var(--pax-white);
  }
  .table-pagination :global(.bx--pagination__button) {
    border-left-color: var(--pax-sub-new);
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--pagination__button .bx--btn__icon),
  .table-pagination :global(.bx--pagination__button .bx--btn__icon path) {
    fill: currentColor !important;
  }
  .table-pagination :global(.bx--pagination__button:hover) {
    background: var(--pax-sub-new);
    color: var(--pax-white);
  }
  .table-pagination :global(.bx--pagination__button:disabled) {
    background: var(--pax-black);
    color: var(--pax-grey);
  }
  .table-scroll :global(.bx--data-table) {
    width: 100%;
    font-size: 0.8125rem;
  }
  .table-scroll :global(.bx--data-table th),
  .table-scroll :global(.bx--data-table td) {
    border-right: 0;
    border-bottom: 1px solid var(--pax-smoke);
    color: var(--pax-black);
    text-align: left;
    vertical-align: middle;
  }
  .table-scroll :global(.bx--data-table th) {
    height: 2.5rem;
    padding: 0.5rem 1rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1;
  }
  .table-scroll :global(.bx--data-table td) {
    padding: 0.75rem 1rem;
  }
  .table-scroll :global(.bx--data-table th:first-child) {
    border-radius: 0.5rem 0 0 0 !important;
  }
  .table-scroll :global(.bx--data-table th:last-child) {
    border-radius: 0 0.5rem 0 0 !important;
  }
  .table-scroll :global(.bx--data-table th .bx--table-header-label) {
    color: var(--pax-white);
  }
  .table-scroll :global(.bx--table-sort),
  .table-scroll :global(.bx--table-sort:hover) {
    min-height: 0;
    padding: 0;
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .table-scroll :global(.bx--table-sort__icon),
  .table-scroll :global(.bx--table-sort__icon-unsorted) {
    fill: var(--pax-white);
  }
  .table-scroll
    :global(
      .custom-table.zebra-rows .bx--data-table tbody tr:nth-child(odd) td
    ) {
    background-color: #ffffff !important;
  }
  .table-scroll
    :global(
      .custom-table.zebra-rows .bx--data-table tbody tr:nth-child(even) td
    ) {
    background-color: #f4f4f4 !important;
  }
  .table-scroll :global(.bx--data-table th:last-child),
  .table-scroll :global(.bx--data-table td:last-child) {
    width: 6rem;
    padding: 0.5rem;
    text-align: center;
    white-space: nowrap;
  }
  .empty-state {
    padding: 1.5rem;
    border-bottom: 1px solid var(--pax-smoke);
    color: var(--pax-sub);
    text-align: center;
  }
</style>
